import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  ModalProps,
  Stack,
} from '@mui/material';
import { Logo } from 'components/logos/logo';
import { Scrollbar } from 'components/scrollbar';
import { useAuthContext } from 'contexts/auth-context';
import { LogoutButton } from 'components/auth/logout-button';
import terraformEnvironment from 'environment';
import { PermissionsTypes } from 'model/role-permissions';
import { readTasks } from 'utils/api-client';
import type { NavItem } from './config';
import { items } from './config';
import { SideNavItem } from './side-nav-item';
import styles from './side-nav.module.scss';

interface Props {
  /*
   * If true, the side navigation will be always visible and part of the page layout,
   * otherwise the side navigation will be rendered as a drawer that overlaps the rest
   * of the page's content and has its visibility controlled by the `open` prop
   */
  permanent: boolean;
  /* Callback to execute when the user closes the side navigation (only if `permanent` is false) */
  onClose?: ModalProps['onClose'];
  /* Whether the side navigation is open (only if `permanent` is false) */
  open: boolean;
}

export const SideNav = ({ open, onClose = () => { }, permanent }: Props) => {
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const [visibleItems, setVisibleItems] = useState<NavItem[]>([]);
  const [taskCount, setTaskCount] = useState(0);

  // Refetch task count whenever the route changes — gives the badge
  // a fresh value after the user actions a task. Cheap (one read all
  // open tasks). Silent on failure: badge just stays at last value.
  useEffect(() => {
    if (!user || !user.ownPermissions || !user.ownPermissions.tasks) {
      return () => {};
    }
    if (user.ownPermissions.tasks.resource.includes(PermissionsTypes.Deny)) {
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const tasks = await readTasks();
        if (!cancelled) setTaskCount((tasks || []).length);
      } catch {
        // leave stale count
      }
    })();
    return () => { cancelled = true; };
  }, [user, pathname]);

  useEffect(() => {
    setVisibleItems(items.filter((item) => {
      if (item.mainResource && item.mainResource.length > 0) {
        // Filter the items by the user's permissions
        if (!user || !Object.keys(user.ownPermissions).length) {
          return false;
        }
        // Allow if ANY of the mainResources is permitted (except for 'settings')
        return item.mainResource.some((resource) => {
          if (resource === 'settings') return true;
          const perm = user.ownPermissions[resource];
          return perm && !(perm.resource.includes(PermissionsTypes.Deny));
        });
      }
      return true;
    }));
  }, [user, pathname]);

  const content = (
    <Scrollbar
      className={styles.side_nav_scrollbar}
    >
      <Box
        className={styles.side_nav_column}
      >
        <Box className={styles.header}>
          <Logo width={150} href={visibleItems.length > 0 ? visibleItems[0].path : '/'} />
        </Box>
        {/* <Divider className={styles.divider} /> */}
        <Box
          component="nav"
          sx={{
            flexGrow: 1,
            px: 2,
            py: 3,
          }}
        >
          <Stack
            component="ul"
            spacing={0.5}
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
            }}
          >
            {(terraformEnvironment !== 'prod')
              && (
                <SideNavItem
                  active={false}
                  alert
                  title={`Environment:${terraformEnvironment}`}
                />
              )}
            {visibleItems.map((item) => {
              const active = item.path ? (pathname === item.path) : false;
              const isTasksItem = item.mainResource && item.mainResource.includes('tasks');
              return (
                <SideNavItem
                  active={active}
                  icon={item.icon}
                  key={item.title}
                  path={item.path}
                  title={item.title}
                  badgeCount={isTasksItem ? taskCount : 0}
                />
              );
            })}
          </Stack>
        </Box>
        <Box className={styles.side_nav_footer}>
          <LogoutButton />
        </Box>
      </Box>
    </Scrollbar>
  );

  const navStyle = [styles.side_nav_root];

  if (permanent) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          className: navStyle.join(' '),
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }
  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        className: navStyle.join(' '),
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 100,
      }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};
